import Head from "next/head";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { getUser } from "/redux/action-creaters/user";
import { getBacket } from "/redux/action-creaters/basket";
import { useRouter } from "next/router";
import { getEdostMinDays } from "/redux/action-creaters/location";
import { debounce } from "lodash";
import dynamic from "next/dynamic";
import MobileMenu from "./mobileMenu";
import TopSections from "./topSections";
import styles from "./index.module.scss";
import Footer from "./footer";
import Header from "./header";
import InternalLinks from "./internalLinks";
import { fetchSections } from "/redux/action-creaters/sections";
import BackForm from "../../common/backForm";
import Cookie from "./cookie";
import useHasScrolled from "../../../hooks/useHasScrolled";
import errorSent from "../../../utils/errors/errorSent";
import { setTextCity } from "../../../utils";

import E2EGuard from "../../common/e2eGuard";
import { BACK_FORM_TEST_SCRIPT } from "../../../utils/abTestsClient";

const Popups = dynamic(() => import("../../popups"), {
  ssr: false,
  loading: () => null,
});

const AdminPanel = dynamic(() => import("../../common/adminPanel"), {
  ssr: false,
  loading: () => null,
});

export default function Layout({
  children,
  isIndex = true,
  host = "",
  scripts,
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isShowTopSections } = useSelector((state) => state.pageState);
  const { isShowMobileMenu } = useSelector((state) => state.pageState);
  const { title, description, canonical, openGraphTags, jsonLdScripts } =
    useSelector((state) => state.meta);
  const { domainData } = useSelector((state) => state.contacts);

  const { cityName, deliveryCityData, deliveryCityDataLoaded } = useSelector(
    (state) => state.location,
  );
  const { contacts, shops } = useSelector((state) => state.contacts);

  const deliveryCity = useMemo(() => {
    if (
      deliveryCityData?.region === "Ленинградская" ||
      deliveryCityData?.region === "Санкт-Петербург"
    ) {
      return "Санкт-Петербург";
    }
    if (
      deliveryCityData?.region === "Московская" ||
      deliveryCityData?.region === "Москва"
    ) {
      return "Москва";
    }
    return cityName;
  }, [cityName, deliveryCityData]);

  useEffect(() => {
    if (
      contacts?.name === "Центральный" &&
      deliveryCityDataLoaded &&
      deliveryCity !== "Россия"
    ) {
      dispatch(
        getEdostMinDays(
          deliveryCity,
          deliveryCityData?.region,
          deliveryCityData?.postal_code,
        ),
      );
    }
  }, [contacts, deliveryCity, deliveryCityDataLoaded]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(getUser());
    }, 2000);
  }, []);

  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(getBacket());
  }, [router]);

  const { sections } = useSelector((state) => state.sections);
  useEffect(() => {
    if (sections?.length === 0) {
      dispatch(fetchSections());
    }
  }, [sections]);

  const { basket } = useSelector((state) => state.cart);

  useEffect(() => {
    if (router.asPath.indexOf("/catalog/farkopy/") > -1) {
      (function (d, w) {
        const n = d.getElementsByTagName("script")[0];

        const s = d.createElement("script");

        s.type = "text/javascript";

        s.async = true;

        s.src = `https://victorycorp.ru/index.php?ref=${
          d.referrer
        }&page=${encodeURIComponent(w.location.href)}`;

        n.parentNode.insertBefore(s, n);
      })(document, window);
    }
  }, [router]);

  const [scrollTop, setScrollTop] = useState(0);

  const isScrolled = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return (
      (window.innerWidth > 991 && scrollTop > 45) ||
      scrollTop > 30 ||
      isShowMobileMenu
    );
  }, [scrollTop]);

  const handleScroll = (e) => {
    setScrollTop(e.target.documentElement.scrollTop);
  };

  const debouncedScroll = debounce(handleScroll, 100);

  useEffect(() => {
    window.addEventListener("scroll", debouncedScroll);

    return () => {
      window.removeEventListener("scroll", debouncedScroll);
      debouncedScroll.cancel();
    };
  }, []);

  useEffect(() => {
    // Только индексируемые страницы: у 404 и личного кабинета (noindex) мета
    // в сторе пустая намеренно — 404 задаёт свою в Head, — и они давали ~90%
    // событий, за которыми терялись реальные страницы без меты.
    if (isIndex && (!title || !description)) {
      errorSent("errorHandled", "emptyMetaTitleOrDescription");
    }
  }, [title, description]);

  // Плавающие элементы не должны закрывать первый экран (UX-04).
  // Класс на body гасит виджет рейтинга — он приходит внешним скриптом,
  // из React им не управлять.
  const hasScrolled = useHasScrolled();

  useEffect(() => {
    document.body.classList.toggle("scrolled", hasScrolled);
  }, [hasScrolled]);

  return (
    <>
      <Head>
        <link rel="preload" as="image/svg+xml" href="icons.svg" />
        <script src="/js/farkop-chat.js?v=2" defer key="farkop-chat" />
        {title && <title>{setTextCity(host, domainData, title)}</title>}
        {description && (
          <meta
            name="description"
            content={setTextCity(host, domainData, description)}
            key="desc"
          />
        )}
        <link rel="shortcut icon" href="/favicon.ico" type="image/png" />
        <meta
          httpEquiv="Content-Type"
          content="text/html; charset=UTF-8"
        ></meta>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content={isIndex ? "all" : "noindex"} />
        {openGraphTags.map((tag, index) => (
          <meta
            key={`${tag.property}-${index}`}
            property={tag.property}
            content={tag.content}
          />
        ))}
        {(jsonLdScripts ?? []).map((jsonLdScript, index) => (
          <script
            key={`json-ld-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: jsonLdScript }}
          />
        ))}
        {domainData?.yandexTag && (
          <meta
            name="yandex-verification"
            content={domainData.yandexTag}
          ></meta>
        )}
        {domainData?.googleTag && (
          <meta
            name="google-site-verification"
            content={domainData.googleTag}
          ></meta>
        )}
        <>
          {canonical
            .filter(
              (linkTag) =>
                linkTag &&
                typeof linkTag.href === "string" &&
                linkTag.href &&
                linkTag.rel,
            )
            .map((linkTag) => (
              <link
                key={`${linkTag.rel}:${linkTag.href}`}
                rel={linkTag.rel}
                href={linkTag.href}
              />
            ))}
        </>
        {host === "farkop.ru" && (
          <meta
            name="msvalidate.01"
            content="2A3993BC6CD41DA6496A729BCB0E39C1"
          ></meta>
        )}
      </Head>
      <Header
        contacts={contacts}
        shops={shops}
        user={user}
        basket={basket}
        isScrolled={isScrolled}
      />
      <div
        className={clsx(
          (isShowTopSections || isShowMobileMenu) && styles.Hidden,
        )}
      >
        {children}
        <InternalLinks />
        <Footer host={host} />
      </div>
      {isShowTopSections && <TopSections />}
      {isShowMobileMenu && <MobileMenu user={user} contacts={contacts} />}
      {!isShowTopSections && !isShowMobileMenu && (
        <>
          <Popups />
        </>
      )}
      {hasScrolled && <BackForm basket={basket} contacts={contacts} />}
      <Cookie />
      {(user?.IS_ADMIN ||
        user?.IS_MANAGER ||
        Boolean(router.query.isManager)) && <AdminPanel />}
      <E2EGuard />
      <script dangerouslySetInnerHTML={{ __html: BACK_FORM_TEST_SCRIPT }} />
      <div dangerouslySetInnerHTML={{ __html: scripts }} />
      <script src="/js/collector.js?v=2" defer />
    </>
  );
}
